import React from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  Leaf, 
  Shield, 
  Globe, 
  DollarSign, 
  Users, 
  Network,
  BookOpen,
  Zap,
  Sprout,
  Brain
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
  category: 'general' | 'technical' | 'pricing' | 'support';
  icon: React.ReactNode;
}

const FAQsPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [openFAQs, setOpenFAQs] = useState<number[]>([]);

  const toggleFAQ = (index: number) => {
    if (openFAQs.includes(index)) {
      setOpenFAQs(openFAQs.filter(item => item !== index));
    } else {
      setOpenFAQs([...openFAQs, index]);
    }
  };

  const faqs: FAQItem[] = [
    {
      question: "What is AVN-Earth?",
      answer: (
        <div>
          <p className="mb-4">
            AVN-Earth is our groundbreaking technology platform that ensures complete transparency and ethical practices throughout the agricultural supply chain, from farm to table.
          </p>
          <p>
            It combines AI, blockchain, and IoT technologies to verify that food is produced without exploitation, while promoting sustainable and regenerative farming practices.
          </p>
        </div>
      ),
      category: 'general',
      icon: <Shield className="h-5 w-5 text-avn-purple-600" />
    },
    {
      question: "How does AVN verify ethical practices?",
      answer: (
        <div>
          <p className="mb-4">
            AVN uses a multi-layered verification approach:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>AI-powered monitoring of supply chain data</li>
            <li>On-the-ground verification through our network of trusted partners</li>
            <li>Blockchain technology to create immutable records</li>
            <li>Community-based reporting and feedback mechanisms</li>
          </ul>
          <p>
            This comprehensive approach ensures that ethical claims are backed by verifiable data and real-world checks.
          </p>
        </div>
      ),
      category: 'technical',
      icon: <Brain className="h-5 w-5 text-blue-600" />
    },
    {
      question: "What is regenerative agriculture?",
      answer: (
        <div>
          <p className="mb-4">
            Regenerative agriculture is a conservation and rehabilitation approach to farming that:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>Focuses on rebuilding soil organic matter</li>
            <li>Restores degraded soil biodiversity</li>
            <li>Improves the water cycle</li>
            <li>Enhances ecosystem services</li>
            <li>Increases resilience to climate fluctuation</li>
          </ul>
          <p>
            Unlike conventional farming, which often depletes resources, regenerative practices actively improve the environment while producing food.
          </p>
        </div>
      ),
      category: 'general',
      icon: <Leaf className="h-5 w-5 text-green-600" />
    },
    {
      question: "How can I join the AVN network?",
      answer: (
        <div>
          <p className="mb-4">
            Joining the AVN network is simple:
          </p>
          <ol className="list-decimal pl-5 space-y-2 mb-4">
            <li>Sign up for an account on our platform</li>
            <li>Complete your profile with details about your role in the food system</li>
            <li>Connect with other members and access our resources</li>
            <li>Implement our verification tools if you're a producer or processor</li>
          </ol>
          <p>
            We welcome all stakeholders in the food system, from farmers to consumers and everyone in between.
          </p>
        </div>
      ),
      category: 'general',
      icon: <Network className="h-5 w-5 text-avn-purple-600" />
    },
    {
      question: "What does it cost to use AVN?",
      answer: (
        <div>
          <p className="mb-4">
            AVN is currently free to use while in beta:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Current Status:</strong> Free for all users during our beta phase</li>
            <li><strong>Future Plans:</strong> Multiple tiers to accommodate different needs</li>
            <li><strong>Pricing:</strong> To be announced in the future</li>
            <li><strong>Enterprise:</strong> Custom solutions for large organizations will be available</li>
          </ul>
          <p>
            We're committed to making ethical verification accessible to all, with plans to offer subsidized rates for smallholder farmers in developing regions when pricing is introduced.
          </p>
        </div>
      ),
      category: 'pricing',
      icon: <DollarSign className="h-5 w-5 text-green-600" />
    },
    {
      question: "How does AVN help farmers?",
      answer: (
        <div>
          <p className="mb-4">
            AVN provides multiple benefits to farmers:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>Access to premium markets that value ethical production</li>
            <li>Data-driven insights to optimize farming practices</li>
            <li>Reduced resource usage through smart farming techniques</li>
            <li>Connection to a global network of buyers and partners</li>
            <li>Training and resources for implementing sustainable practices</li>
          </ul>
          <p>
            Our goal is to ensure farmers receive fair compensation while adopting practices that benefit both people and planet.
          </p>
        </div>
      ),
      category: 'general',
      icon: <Sprout className="h-5 w-5 text-green-600" />
    },
    {
      question: "What technology does AVN use?",
      answer: (
        <div>
          <p className="mb-4">
            AVN integrates several cutting-edge technologies:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Artificial Intelligence:</strong> For data analysis, pattern recognition, and predictive insights</li>
            <li><strong>Blockchain:</strong> To create immutable records of supply chain transactions</li>
            <li><strong>IoT Sensors:</strong> To collect real-time data from farms and processing facilities</li>
            <li><strong>Mobile Applications:</strong> For easy access and data collection in the field</li>
            <li><strong>Cloud Computing:</strong> For scalable processing and storage</li>
          </ul>
          <p>
            These technologies work together to create a comprehensive system for verifying ethical practices and optimizing agricultural operations.
          </p>
        </div>
      ),
      category: 'technical',
      icon: <Zap className="h-5 w-5 text-yellow-600" />
    },
    {
      question: "What is the REGEN Campus?",
      answer: (
        <div>
          <p className="mb-4">
            REGEN Campus is our innovative educational hub that combines hands-on learning with cutting-edge technology to create a comprehensive training ecosystem for sustainable agriculture.
          </p>
          <p className="mb-4">
            Our campuses feature:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>Demonstration farms showcasing regenerative practices</li>
            <li>Technology labs for learning about AgTech solutions</li>
            <li>Classroom and online learning opportunities</li>
            <li>Community spaces for knowledge sharing</li>
          </ul>
          <p>
            We currently have campuses in Uganda and the UK, with plans to expand to more locations.
          </p>
        </div>
      ),
      category: 'general',
      icon: <BookOpen className="h-5 w-5 text-avn-purple-600" />
    },
    {
      question: "How does AVN address climate change?",
      answer: (
        <div>
          <p className="mb-4">
            AVN addresses climate change through multiple approaches:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>Promoting regenerative agriculture practices that sequester carbon</li>
            <li>Reducing resource usage through precision farming techniques</li>
            <li>Minimizing food waste through better supply chain management</li>
            <li>Supporting agroforestry and other carbon-negative farming systems</li>
            <li>Providing climate-resilient farming strategies to adapt to changing conditions</li>
          </ul>
          <p>
            Our goal is to transform agriculture from a carbon source to a carbon sink while improving resilience to climate impacts.
          </p>
        </div>
      ),
      category: 'general',
      icon: <Globe className="h-5 w-5 text-blue-600" />
    },
    {
      question: "How can I get support?",
      answer: (
        <div>
          <p className="mb-4">
            We offer multiple support channels:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Email Support:</strong> info@avnearth.com</li>
            <li><strong>Phone Support:</strong> +44 794 950 0433</li>
            <li><strong>Community Forum:</strong> Connect with other users to share knowledge</li>
            <li><strong>Knowledge Base:</strong> Access our comprehensive documentation</li>
            <li><strong>Webinars:</strong> Regular training sessions on platform features</li>
          </ul>
          <p>
            Premium and Enterprise users also receive dedicated support representatives.
          </p>
        </div>
      ),
      category: 'support',
      icon: <Users className="h-5 w-5 text-avn-purple-600" />
    }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-12">
        <SectionHeader
          title="Frequently Asked Questions"
          subtitle="Find answers to common questions about AVN, our technology, and how we're transforming agriculture."
          icon={<HelpCircle className="h-5 w-5" />}
          badgeText="Knowledge Base"
        />

        {/* Category Filters */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-6 py-3 rounded-lg transition-colors ${
                activeCategory === 'all'
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All Questions
            </button>
            <button
              onClick={() => setActiveCategory('general')}
              className={`px-6 py-3 rounded-lg transition-colors ${
                activeCategory === 'general'
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setActiveCategory('technical')}
              className={`px-6 py-3 rounded-lg transition-colors ${
                activeCategory === 'technical'
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Technical
            </button>
            <button
              onClick={() => setActiveCategory('pricing')}
              className={`px-6 py-3 rounded-lg transition-colors ${
                activeCategory === 'pricing'
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Pricing
            </button>
            <button
              onClick={() => setActiveCategory('support')}
              className={`px-6 py-3 rounded-lg transition-colors ${
                activeCategory === 'support'
                  ? 'bg-avn-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Support
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6">
            {filteredFAQs.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 p-2 rounded-lg">
                        {faq.icon}
                      </div>
                      <h3 className="font-semibold text-lg">{faq.question}</h3>
                    </div>
                    <motion.div
                      animate={{ rotate: openFAQs.includes(index) ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {openFAQs.includes(index) ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </motion.div>
                  </button>
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: openFAQs.includes(index) ? 'auto' : 0,
                      opacity: openFAQs.includes(index) ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.answer}
                    </div>
                  </motion.div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* Still Have Questions */}
        <div className="max-w-3xl mx-auto mt-16">
          <AnimatedSection>
            <div className="bg-avn-purple-50 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
              <p className="text-gray-600 mb-6">
                Our team is here to help. Reach out to us and we'll get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:info@avnearth.com"
                  className="bg-avn-purple-600 text-white px-6 py-3 rounded-lg hover:bg-avn-purple-700 transition-colors inline-flex items-center justify-center gap-2"
                >
                  Contact Support
                </a>
                <a
                  href="tel:+447949500433"
                  className="bg-white text-avn-purple-700 px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 border border-avn-purple-200"
                >
                  Call Us
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default FAQsPage;